export type AdaptiveGridThemeMode = 'light' | 'dark';

export interface AdaptiveViewportGridContext {
  canvas: HTMLCanvasElement;
  viewport: HTMLElement;
  root: HTMLElement;
  scale: number;
  scrollLeft: number;
  scrollTop: number;
  themeMode: AdaptiveGridThemeMode;
}

interface RgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

interface VisibleSurface {
  left: number;
  top: number;
  right: number;
  bottom: number;
  tone: 'light' | 'dark';
  order: number;
  borderRadii: SurfaceBorderRadii | null;
}

interface SurfaceCornerRadius {
  x: number;
  y: number;
}

interface SurfaceBorderRadii {
  topLeft: SurfaceCornerRadius;
  topRight: SurfaceCornerRadius;
  bottomRight: SurfaceCornerRadius;
  bottomLeft: SurfaceCornerRadius;
}

interface ToneInterval {
  start: number;
  end: number;
  tone: 'light' | 'dark';
  order: number;
}

interface CachedSurfaceMap {
  root: HTMLElement;
  themeMode: AdaptiveGridThemeMode;
  logicalWidth: number;
  logicalHeight: number;
  surfaces: VisibleSurface[];
  verticalBuckets: VisibleSurface[][];
  horizontalBuckets: VisibleSurface[][];
  fallbackTone: 'light' | 'dark';
}

const GRID_MINOR_STEP = 20;
const GRID_MAJOR_STEP = 100;
const MIN_SURFACE_ALPHA = 0.06;
const MIN_SURFACE_AREA = 4096;
const MIN_SURFACE_DIMENSION = 48;
const LIGHT_SURFACE_LUMINANCE_THRESHOLD = 0.58;
const LIGHT_THEME_FALLBACK: RgbaColor = { r: 255, g: 255, b: 255, a: 1 };
const DARK_THEME_FALLBACK: RgbaColor = { r: 15, g: 23, b: 42, a: 1 };
const DARK_MINOR_STROKE = 'rgba(15, 23, 42, 0.24)';
const DARK_MAJOR_STROKE = 'rgba(15, 23, 42, 0.4)';
const LIGHT_MINOR_STROKE = 'rgba(255, 255, 255, 0.38)';
const LIGHT_MAJOR_STROKE = 'rgba(255, 255, 255, 0.58)';

export class AdaptiveViewportGridRenderer {
  private animationFrameId: number | null = null;
  private mutationObserver: MutationObserver | null = null;
  private observedRoot: HTMLElement | null = null;
  private backgroundColorCache = new WeakMap<HTMLElement, RgbaColor | null>();
  private surfaceMapCache: CachedSurfaceMap | null = null;
  private surfaceMapDirty = true;

  constructor(private readonly resolveContext: () => AdaptiveViewportGridContext | null) {}

  scheduleRender(): void {
    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
      this.renderNow();
      return;
    }

    if (this.animationFrameId !== null) {
      return;
    }

    this.animationFrameId = requestAnimationFrame(() => {
      this.animationFrameId = null;
      this.renderNow();
    });
  }

  refreshObservation(): void {
    const context = this.resolveContext();
    const nextRoot = context?.root ?? null;
    if (nextRoot === this.observedRoot) {
      return;
    }

    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.observedRoot = nextRoot;
    this.backgroundColorCache = new WeakMap<HTMLElement, RgbaColor | null>();
    this.surfaceMapCache = null;
    this.surfaceMapDirty = true;

    if (!nextRoot || typeof MutationObserver === 'undefined') {
      return;
    }

    this.mutationObserver = new MutationObserver(() => {
      this.backgroundColorCache = new WeakMap<HTMLElement, RgbaColor | null>();
      this.surfaceMapDirty = true;
      this.scheduleRender();
    });
    this.mutationObserver.observe(nextRoot, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
  }

  disconnect(): void {
    if (this.animationFrameId !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.observedRoot = null;
    this.backgroundColorCache = new WeakMap<HTMLElement, RgbaColor | null>();
    this.surfaceMapCache = null;
    this.surfaceMapDirty = true;
  }

  private renderNow(): void {
    const context = this.resolveContext();
    if (!context) {
      return;
    }

    this.refreshObservation();

    const cssWidth = Math.max(1, context.viewport.clientWidth);
    const cssHeight = Math.max(1, context.viewport.clientHeight);
    const pixelRatio = typeof window === 'undefined' ? 1 : Math.max(1, window.devicePixelRatio || 1);
    const targetWidth = Math.max(1, Math.round(cssWidth * pixelRatio));
    const targetHeight = Math.max(1, Math.round(cssHeight * pixelRatio));

    if (context.canvas.width !== targetWidth || context.canvas.height !== targetHeight) {
      context.canvas.width = targetWidth;
      context.canvas.height = targetHeight;
    }

    const drawingContext = context.canvas.getContext('2d');
    if (!drawingContext) {
      return;
    }

    drawingContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    drawingContext.clearRect(0, 0, cssWidth, cssHeight);

    const lineScale = Math.max(context.scale, 0.05);
    const minorLineWidth = 1 / lineScale;
    const majorLineWidth = 1.35 / lineScale;
    const crispOffset = 0.5 / lineScale;

    const surfaceMap = this.getSurfaceMap({
      root: context.root,
      scale: context.scale,
      themeMode: context.themeMode
    });

    this.drawVerticalLines({
      drawingContext,
      cssWidth,
      cssHeight,
      scrollLeft: context.scrollLeft,
      scrollTop: context.scrollTop,
      lineWidthMinor: minorLineWidth,
      lineWidthMajor: majorLineWidth,
      crispOffset,
      surfaceMap
    });

    this.drawHorizontalLines({
      drawingContext,
      cssWidth,
      cssHeight,
      scrollLeft: context.scrollLeft,
      scrollTop: context.scrollTop,
      lineWidthMinor: minorLineWidth,
      lineWidthMajor: majorLineWidth,
      crispOffset,
      surfaceMap
    });
  }

  private getSurfaceMap(options: {
    root: HTMLElement;
    scale: number;
    themeMode: AdaptiveGridThemeMode;
  }): CachedSurfaceMap {
    const { root, scale, themeMode } = options;
    const normalizedScale = Math.max(scale, 0.05);
    const rootRect = root.getBoundingClientRect();
    const logicalWidth = Math.max(1, rootRect.width / normalizedScale);
    const logicalHeight = Math.max(1, rootRect.height / normalizedScale);

    if (
      this.surfaceMapCache
      && !this.surfaceMapDirty
      && this.surfaceMapCache.root === root
      && this.surfaceMapCache.themeMode === themeMode
      && Math.abs(this.surfaceMapCache.logicalWidth - logicalWidth) <= 0.5
      && Math.abs(this.surfaceMapCache.logicalHeight - logicalHeight) <= 0.5
    ) {
      return this.surfaceMapCache;
    }

    const fallbackColor = themeMode === 'dark' ? DARK_THEME_FALLBACK : LIGHT_THEME_FALLBACK;
    const rootBackground = this.resolveBackgroundColor(root);
    const fallbackComposite = rootBackground
      ? this.composite(rootBackground, fallbackColor)
      : fallbackColor;
    const fallbackTone = this.resolveTone(fallbackComposite);
    const surfaces: VisibleSurface[] = [];

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
    let current = walker.currentNode as HTMLElement | null;
    let order = 0;

    while (current) {
      if (current !== root && !this.shouldIgnoreSurfaceElement(current)) {
        const rect = current.getBoundingClientRect();
        const backgroundColor = this.resolveBackgroundColor(current);
        if (backgroundColor && backgroundColor.a >= MIN_SURFACE_ALPHA) {
          const logicalLeft = (rect.left - rootRect.left) / normalizedScale;
          const logicalTop = (rect.top - rootRect.top) / normalizedScale;
          const width = rect.width / normalizedScale;
          const height = rect.height / normalizedScale;
          const logicalRight = logicalLeft + width;
          const logicalBottom = logicalTop + height;

          if (
            width > 0
            && height > 0
            && width * height >= MIN_SURFACE_AREA
            && (width >= MIN_SURFACE_DIMENSION || height >= MIN_SURFACE_DIMENSION)
          ) {
            surfaces.push({
              left: logicalLeft,
              top: logicalTop,
              right: logicalRight,
              bottom: logicalBottom,
              tone: this.resolveTone(this.composite(backgroundColor, fallbackComposite)),
              order,
              borderRadii: this.resolveBorderRadii(current, width, height)
            });
          }
        }
      }

      order += 1;
      current = walker.nextNode() as HTMLElement | null;
    }

    this.surfaceMapDirty = false;
    this.surfaceMapCache = {
      root,
      themeMode,
      logicalWidth,
      logicalHeight,
      surfaces,
      verticalBuckets: this.buildBuckets(surfaces, logicalWidth, 'vertical'),
      horizontalBuckets: this.buildBuckets(surfaces, logicalHeight, 'horizontal'),
      fallbackTone
    };

    return this.surfaceMapCache;
  }

  private drawVerticalLines(options: {
    drawingContext: CanvasRenderingContext2D;
    cssWidth: number;
    cssHeight: number;
    scrollLeft: number;
    scrollTop: number;
    lineWidthMinor: number;
    lineWidthMajor: number;
    crispOffset: number;
    surfaceMap: CachedSurfaceMap;
  }): void {
    const {
      drawingContext,
      cssWidth,
      cssHeight,
      scrollLeft,
      scrollTop,
      lineWidthMinor,
      lineWidthMajor,
      crispOffset,
      surfaceMap
    } = options;

    const start = Math.floor(scrollLeft / GRID_MINOR_STEP) * GRID_MINOR_STEP;
    const end = scrollLeft + cssWidth;

    for (let logicalX = start; logicalX <= end + GRID_MINOR_STEP; logicalX += GRID_MINOR_STEP) {
      const visibleX = logicalX - scrollLeft;
      if (visibleX < 0 || visibleX > cssWidth) {
        continue;
      }

      const isMajor = logicalX % GRID_MAJOR_STEP === 0;
      drawingContext.lineWidth = isMajor ? lineWidthMajor : lineWidthMinor;
      const runs = this.resolveToneRunsForVerticalLine(logicalX, scrollTop, scrollTop + cssHeight, surfaceMap);

      for (const run of runs) {
        drawingContext.strokeStyle = this.resolveStrokeStyle(run.tone, isMajor);
        drawingContext.beginPath();
        drawingContext.moveTo(visibleX + crispOffset, run.start);
        drawingContext.lineTo(visibleX + crispOffset, run.end);
        drawingContext.stroke();
      }
    }
  }

  private drawHorizontalLines(options: {
    drawingContext: CanvasRenderingContext2D;
    cssWidth: number;
    cssHeight: number;
    scrollLeft: number;
    scrollTop: number;
    lineWidthMinor: number;
    lineWidthMajor: number;
    crispOffset: number;
    surfaceMap: CachedSurfaceMap;
  }): void {
    const {
      drawingContext,
      cssWidth,
      cssHeight,
      scrollLeft,
      scrollTop,
      lineWidthMinor,
      lineWidthMajor,
      crispOffset,
      surfaceMap
    } = options;

    const start = Math.floor(scrollTop / GRID_MINOR_STEP) * GRID_MINOR_STEP;
    const end = scrollTop + cssHeight;

    for (let logicalY = start; logicalY <= end + GRID_MINOR_STEP; logicalY += GRID_MINOR_STEP) {
      const visibleY = logicalY - scrollTop;
      if (visibleY < 0 || visibleY > cssHeight) {
        continue;
      }

      const isMajor = logicalY % GRID_MAJOR_STEP === 0;
      drawingContext.lineWidth = isMajor ? lineWidthMajor : lineWidthMinor;
      const runs = this.resolveToneRunsForHorizontalLine(logicalY, scrollLeft, scrollLeft + cssWidth, surfaceMap);

      for (const run of runs) {
        drawingContext.strokeStyle = this.resolveStrokeStyle(run.tone, isMajor);
        drawingContext.beginPath();
        drawingContext.moveTo(run.start, visibleY + crispOffset);
        drawingContext.lineTo(run.end, visibleY + crispOffset);
        drawingContext.stroke();
      }
    }
  }

  private resolveToneRunsForVerticalLine(
    logicalX: number,
    viewportStartY: number,
    viewportEndY: number,
    surfaceMap: CachedSurfaceMap
  ): Array<{ start: number; end: number; tone: 'light' | 'dark' }> {
    const bucketIndex = this.resolveBucketIndex(logicalX, surfaceMap.verticalBuckets.length);
    const relevantSurfaces = surfaceMap.verticalBuckets[bucketIndex] ?? [];
    const relevantIntervals: ToneInterval[] = [];

    for (const surface of relevantSurfaces) {
      if (
        logicalX < surface.left
        || logicalX > surface.right
        || surface.bottom <= viewportStartY
        || surface.top >= viewportEndY
      ) {
        continue;
      }

      const interval = this.resolveVerticalInterval(surface, logicalX);
      if (interval) {
        relevantIntervals.push(interval);
      }
    }

    return this.resolveToneRuns({
      viewportStart: viewportStartY,
      viewportEnd: viewportEndY,
      intervals: relevantIntervals,
      fallbackTone: surfaceMap.fallbackTone
    });
  }

  private resolveToneRunsForHorizontalLine(
    logicalY: number,
    viewportStartX: number,
    viewportEndX: number,
    surfaceMap: CachedSurfaceMap
  ): Array<{ start: number; end: number; tone: 'light' | 'dark' }> {
    const bucketIndex = this.resolveBucketIndex(logicalY, surfaceMap.horizontalBuckets.length);
    const relevantSurfaces = surfaceMap.horizontalBuckets[bucketIndex] ?? [];
    const relevantIntervals: ToneInterval[] = [];

    for (const surface of relevantSurfaces) {
      if (
        logicalY < surface.top
        || logicalY > surface.bottom
        || surface.right <= viewportStartX
        || surface.left >= viewportEndX
      ) {
        continue;
      }

      const interval = this.resolveHorizontalInterval(surface, logicalY);
      if (interval) {
        relevantIntervals.push(interval);
      }
    }

    return this.resolveToneRuns({
      viewportStart: viewportStartX,
      viewportEnd: viewportEndX,
      intervals: relevantIntervals,
      fallbackTone: surfaceMap.fallbackTone
    });
  }

  private resolveToneRuns(options: {
    viewportStart: number;
    viewportEnd: number;
    intervals: ToneInterval[];
    fallbackTone: 'light' | 'dark';
  }): Array<{ start: number; end: number; tone: 'light' | 'dark' }> {
    const { viewportStart, viewportEnd, intervals, fallbackTone } = options;
    const length = Math.max(0, viewportEnd - viewportStart);
    if (!intervals.length) {
      return [{ start: 0, end: length, tone: fallbackTone }];
    }

    const boundaries = new Set<number>([viewportStart, viewportEnd]);
    for (const interval of intervals) {
      boundaries.add(clamp(interval.start, viewportStart, viewportEnd));
      boundaries.add(clamp(interval.end, viewportStart, viewportEnd));
    }

    const sortedBoundaries = Array.from(boundaries).sort((left, right) => left - right);
    const runs: Array<{ start: number; end: number; tone: 'light' | 'dark' }> = [];

    for (let index = 0; index < sortedBoundaries.length - 1; index += 1) {
      const start = sortedBoundaries[index];
      const end = sortedBoundaries[index + 1];
      if (end - start <= 0.5) {
        continue;
      }

      const midpoint = (start + end) / 2;
      let tone = fallbackTone;
      let topOrder = -1;

      for (const interval of intervals) {
        if (midpoint < interval.start || midpoint > interval.end || interval.order <= topOrder) {
          continue;
        }

        tone = interval.tone;
        topOrder = interval.order;
      }

      const previousRun = runs[runs.length - 1];
      if (previousRun && previousRun.tone === tone) {
        previousRun.end = end - viewportStart;
      } else {
        runs.push({
          start: start - viewportStart,
          end: end - viewportStart,
          tone
        });
      }
    }

    return runs.length ? runs : [{ start: 0, end: length, tone: fallbackTone }];
  }

  private buildBuckets(
    surfaces: VisibleSurface[],
    logicalLength: number,
    axis: 'vertical' | 'horizontal'
  ): VisibleSurface[][] {
    const bucketCount = Math.max(1, Math.ceil(logicalLength / GRID_MINOR_STEP) + 1);
    const buckets = Array.from({ length: bucketCount }, () => [] as VisibleSurface[]);

    for (const surface of surfaces) {
      const start = axis === 'vertical' ? surface.left : surface.top;
      const end = axis === 'vertical' ? surface.right : surface.bottom;
      const startIndex = this.resolveBucketIndex(start, bucketCount);
      const endIndex = this.resolveBucketIndex(end, bucketCount);

      for (let index = startIndex; index <= endIndex; index += 1) {
        buckets[index].push(surface);
      }
    }

    return buckets;
  }

  private resolveBucketIndex(logicalPosition: number, bucketCount: number): number {
    if (bucketCount <= 1) {
      return 0;
    }

    return clamp(Math.floor(logicalPosition / GRID_MINOR_STEP), 0, bucketCount - 1);
  }

  private resolveStrokeStyle(tone: 'light' | 'dark', isMajor: boolean): string {
    return tone === 'light'
      ? (isMajor ? DARK_MAJOR_STROKE : DARK_MINOR_STROKE)
      : (isMajor ? LIGHT_MAJOR_STROKE : LIGHT_MINOR_STROKE);
  }

  private resolveVerticalInterval(surface: VisibleSurface, x: number): ToneInterval | null {
    let start = surface.top;
    let end = surface.bottom;
    const borderRadii = surface.borderRadii;

    if (borderRadii) {
      start = Math.max(
        start,
        this.resolveTopBoundaryForX(surface.left, surface.top, surface.right, x, borderRadii.topLeft, borderRadii.topRight)
      );
      end = Math.min(
        end,
        this.resolveBottomBoundaryForX(surface.left, surface.top, surface.right, surface.bottom, x, borderRadii.bottomLeft, borderRadii.bottomRight)
      );
    }

    if (end - start <= 0.5) {
      return null;
    }

    return {
      start,
      end,
      tone: surface.tone,
      order: surface.order
    };
  }

  private resolveHorizontalInterval(surface: VisibleSurface, y: number): ToneInterval | null {
    let start = surface.left;
    let end = surface.right;
    const borderRadii = surface.borderRadii;

    if (borderRadii) {
      start = Math.max(
        start,
        this.resolveLeftBoundaryForY(surface.left, surface.top, surface.bottom, y, borderRadii.topLeft, borderRadii.bottomLeft)
      );
      end = Math.min(
        end,
        this.resolveRightBoundaryForY(surface.left, surface.top, surface.right, surface.bottom, y, borderRadii.topRight, borderRadii.bottomRight)
      );
    }

    if (end - start <= 0.5) {
      return null;
    }

    return {
      start,
      end,
      tone: surface.tone,
      order: surface.order
    };
  }

  private resolveTopBoundaryForX(
    left: number,
    top: number,
    right: number,
    x: number,
    topLeft: SurfaceCornerRadius,
    topRight: SurfaceCornerRadius
  ): number {
    let boundary = top;

    if (topLeft.x > 0 && topLeft.y > 0 && x < left + topLeft.x) {
      boundary = Math.max(boundary, this.ellipseTopBoundary(x, left + topLeft.x, top + topLeft.y, topLeft.x, topLeft.y));
    }

    if (topRight.x > 0 && topRight.y > 0 && x > right - topRight.x) {
      boundary = Math.max(boundary, this.ellipseTopBoundary(x, right - topRight.x, top + topRight.y, topRight.x, topRight.y));
    }

    return boundary;
  }

  private resolveBottomBoundaryForX(
    left: number,
    top: number,
    right: number,
    bottom: number,
    x: number,
    bottomLeft: SurfaceCornerRadius,
    bottomRight: SurfaceCornerRadius
  ): number {
    let boundary = bottom;

    if (bottomLeft.x > 0 && bottomLeft.y > 0 && x < left + bottomLeft.x) {
      boundary = Math.min(boundary, this.ellipseBottomBoundary(x, left + bottomLeft.x, bottom - bottomLeft.y, bottomLeft.x, bottomLeft.y));
    }

    if (bottomRight.x > 0 && bottomRight.y > 0 && x > right - bottomRight.x) {
      boundary = Math.min(boundary, this.ellipseBottomBoundary(x, right - bottomRight.x, bottom - bottomRight.y, bottomRight.x, bottomRight.y));
    }

    return boundary;
  }

  private resolveLeftBoundaryForY(
    left: number,
    top: number,
    bottom: number,
    y: number,
    topLeft: SurfaceCornerRadius,
    bottomLeft: SurfaceCornerRadius
  ): number {
    let boundary = left;

    if (topLeft.x > 0 && topLeft.y > 0 && y < top + topLeft.y) {
      boundary = Math.max(boundary, this.ellipseLeftBoundary(y, left + topLeft.x, top + topLeft.y, topLeft.x, topLeft.y));
    }

    if (bottomLeft.x > 0 && bottomLeft.y > 0 && y > bottom - bottomLeft.y) {
      boundary = Math.max(boundary, this.ellipseLeftBoundary(y, left + bottomLeft.x, bottom - bottomLeft.y, bottomLeft.x, bottomLeft.y));
    }

    return boundary;
  }

  private resolveRightBoundaryForY(
    left: number,
    top: number,
    right: number,
    bottom: number,
    y: number,
    topRight: SurfaceCornerRadius,
    bottomRight: SurfaceCornerRadius
  ): number {
    let boundary = right;

    if (topRight.x > 0 && topRight.y > 0 && y < top + topRight.y) {
      boundary = Math.min(boundary, this.ellipseRightBoundary(y, right - topRight.x, top + topRight.y, topRight.x, topRight.y));
    }

    if (bottomRight.x > 0 && bottomRight.y > 0 && y > bottom - bottomRight.y) {
      boundary = Math.min(boundary, this.ellipseRightBoundary(y, right - bottomRight.x, bottom - bottomRight.y, bottomRight.x, bottomRight.y));
    }

    return boundary;
  }

  private ellipseTopBoundary(x: number, centerX: number, centerY: number, radiusX: number, radiusY: number): number {
    return centerY - this.ellipseYOffset(x, centerX, radiusX, radiusY);
  }

  private ellipseBottomBoundary(x: number, centerX: number, centerY: number, radiusX: number, radiusY: number): number {
    return centerY + this.ellipseYOffset(x, centerX, radiusX, radiusY);
  }

  private ellipseLeftBoundary(y: number, centerX: number, centerY: number, radiusX: number, radiusY: number): number {
    return centerX - this.ellipseXOffset(y, centerY, radiusX, radiusY);
  }

  private ellipseRightBoundary(y: number, centerX: number, centerY: number, radiusX: number, radiusY: number): number {
    return centerX + this.ellipseXOffset(y, centerY, radiusX, radiusY);
  }

  private ellipseYOffset(x: number, centerX: number, radiusX: number, radiusY: number): number {
    if (radiusX <= 0 || radiusY <= 0) {
      return 0;
    }

    const deltaX = x - centerX;
    const normalized = 1 - ((deltaX * deltaX) / (radiusX * radiusX));
    return radiusY * Math.sqrt(Math.max(0, normalized));
  }

  private ellipseXOffset(y: number, centerY: number, radiusX: number, radiusY: number): number {
    if (radiusX <= 0 || radiusY <= 0) {
      return 0;
    }

    const deltaY = y - centerY;
    const normalized = 1 - ((deltaY * deltaY) / (radiusY * radiusY));
    return radiusX * Math.sqrt(Math.max(0, normalized));
  }

  private resolveBorderRadii(element: HTMLElement, logicalWidth: number, logicalHeight: number): SurfaceBorderRadii | null {
    const computedStyle = getComputedStyle(element);
    const width = Math.max(0, logicalWidth);
    const height = Math.max(0, logicalHeight);

    const topLeft = this.parseCornerRadius(computedStyle.borderTopLeftRadius, width, height);
    const topRight = this.parseCornerRadius(computedStyle.borderTopRightRadius, width, height);
    const bottomRight = this.parseCornerRadius(computedStyle.borderBottomRightRadius, width, height);
    const bottomLeft = this.parseCornerRadius(computedStyle.borderBottomLeftRadius, width, height);

    const horizontalTop = topLeft.x + topRight.x;
    const horizontalBottom = bottomLeft.x + bottomRight.x;
    const verticalLeft = topLeft.y + bottomLeft.y;
    const verticalRight = topRight.y + bottomRight.y;
    const normalizationFactor = Math.min(
      1,
      this.safeRadiusScale(width, horizontalTop),
      this.safeRadiusScale(width, horizontalBottom),
      this.safeRadiusScale(height, verticalLeft),
      this.safeRadiusScale(height, verticalRight)
    );

    const normalizedRadii: SurfaceBorderRadii = {
      topLeft: this.scaleCornerRadius(topLeft, normalizationFactor),
      topRight: this.scaleCornerRadius(topRight, normalizationFactor),
      bottomRight: this.scaleCornerRadius(bottomRight, normalizationFactor),
      bottomLeft: this.scaleCornerRadius(bottomLeft, normalizationFactor)
    };

    const hasVisibleRadius = Object.values(normalizedRadii).some((radius) => radius.x > 0.5 && radius.y > 0.5);
    return hasVisibleRadius ? normalizedRadii : null;
  }

  private parseCornerRadius(
    value: string,
    logicalWidth: number,
    logicalHeight: number
  ): SurfaceCornerRadius {
    const [rawX = '0', rawY = rawX] = value
      .trim()
      .replace(/\//g, ' ')
      .split(/\s+/)
      .filter(Boolean);

    return {
      x: this.parseRadiusLength(rawX, logicalWidth),
      y: this.parseRadiusLength(rawY, logicalHeight)
    };
  }

  private parseRadiusLength(value: string, logicalSize: number): number {
    if (!value) {
      return 0;
    }

    if (value.endsWith('%')) {
      const percentage = Number.parseFloat(value);
      if (!Number.isFinite(percentage)) {
        return 0;
      }

      return Math.max(0, (percentage / 100) * logicalSize);
    }

    const numeric = Number.parseFloat(value);
    if (!Number.isFinite(numeric)) {
      return 0;
    }

    return Math.max(0, numeric);
  }

  private safeRadiusScale(length: number, totalRadius: number): number {
    if (totalRadius <= 0) {
      return 1;
    }

    return length / totalRadius;
  }

  private scaleCornerRadius(radius: SurfaceCornerRadius, factor: number): SurfaceCornerRadius {
    return {
      x: radius.x * factor,
      y: radius.y * factor
    };
  }

  private resolveBackgroundColor(element: HTMLElement): RgbaColor | null {
    if (this.backgroundColorCache.has(element)) {
      return this.backgroundColorCache.get(element) ?? null;
    }

    const backgroundColor = this.parseCssColor(getComputedStyle(element).backgroundColor);
    this.backgroundColorCache.set(element, backgroundColor);
    return backgroundColor;
  }

  private resolveTone(color: RgbaColor): 'light' | 'dark' {
    return this.luminance(color) > LIGHT_SURFACE_LUMINANCE_THRESHOLD ? 'light' : 'dark';
  }

  private shouldIgnoreSurfaceElement(element: HTMLElement): boolean {
    const className = typeof element.className === 'string' ? element.className : '';
    if (
      className.includes('__viewport-grid')
      || className.includes('__viewport-ruler')
      || className.includes('__viewport-ruler-corner')
    ) {
      return true;
    }

    const computedStyle = getComputedStyle(element);
    return computedStyle.display === 'none'
      || computedStyle.visibility === 'hidden'
      || Number.parseFloat(computedStyle.opacity || '1') <= 0;
  }

  private parseCssColor(value: string | null | undefined): RgbaColor | null {
    if (!value) {
      return null;
    }

    const normalized = value.trim().toLowerCase();
    if (!normalized || normalized === 'transparent') {
      return null;
    }

    const rgbaMatch = normalized.match(/^rgba?\(([^)]+)\)$/);
    if (!rgbaMatch) {
      return null;
    }

    const [red = '0', green = '0', blue = '0', alpha = '1'] = rgbaMatch[1].split(',').map((part) => part.trim());
    const parsedAlpha = Number.parseFloat(alpha);

    return {
      r: clampColorChannel(red),
      g: clampColorChannel(green),
      b: clampColorChannel(blue),
      a: clampUnitInterval(Number.isFinite(parsedAlpha) ? parsedAlpha : 1)
    };
  }

  private composite(foreground: RgbaColor, background: RgbaColor): RgbaColor {
    const alpha = foreground.a + (background.a * (1 - foreground.a));
    if (alpha <= 0) {
      return { r: 0, g: 0, b: 0, a: 0 };
    }

    return {
      r: Math.round(((foreground.r * foreground.a) + (background.r * background.a * (1 - foreground.a))) / alpha),
      g: Math.round(((foreground.g * foreground.a) + (background.g * background.a * (1 - foreground.a))) / alpha),
      b: Math.round(((foreground.b * foreground.a) + (background.b * background.a * (1 - foreground.a))) / alpha),
      a: alpha
    };
  }

  private luminance(color: RgbaColor): number {
    const channel = (value: number): number => {
      const normalized = value / 255;
      return normalized <= 0.03928
        ? normalized / 12.92
        : ((normalized + 0.055) / 1.055) ** 2.4;
    };

    return (0.2126 * channel(color.r)) + (0.7152 * channel(color.g)) + (0.0722 * channel(color.b));
  }
}

function clampColorChannel(value: string): number {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }

  return Math.max(0, Math.min(255, Math.round(parsed)));
}

function clampUnitInterval(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
